import { useState } from 'react';

import { Form } from '@components/form';
import { Sidebar, useSidebar, hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCreateMCPServer, useUpdateMCPServer } from 'api/mcp/hooks';
import { MCPServer } from 'api/mcp/requests';

interface MCPServerFormProps {
  existingServer?: MCPServer;
}

export const useMCPServerSidebar = () => {
  return useSidebar(MCPServerSidebar, {
    variant: 'REGULAR',
  });
};
const MCPServerForm = ({ existingServer }: MCPServerFormProps) => {
  const { t } = useScopedTranslate(locale);
  const createMCPServerMutation = useCreateMCPServer();
  const updateMCPServerMutation = useUpdateMCPServer();

  return (
    <Stack space={4}>
      <Form.TextField
        id="label"
        label={t('name')}
        placeholder={t('name')}
        required
        defaultValue={existingServer?.label}
      />
      <Form.Textarea
        id="description"
        label={t('description')}
        placeholder={t('description')}
        defaultValue={existingServer?.description}
      />
      <Form.Submit
        text={existingServer ? t('save') : t('create')}
        disabled={createMCPServerMutation.isLoading || updateMCPServerMutation.isLoading}
      />
    </Stack>
  );
};

interface NewServerDetailsProps {
  server: MCPServer;
}

const NewServerDetails = ({ server }: NewServerDetailsProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={4}>
      <Text font="bold14" text={t('createMCPServerSuccess')} />
      <Stack space={2}>
        <Text font="semibold14" text={t('name')} />
        <Text font="regular14" text={server.label} />
      </Stack>
      {server.description && (
        <Stack space={2}>
          <Text font="semibold14" text={t('description')} />
          <Text font="regular14" text={server.description} />
        </Stack>
      )}
      <Stack space={2}>
        <Text font="semibold14" text={t('clientId')} />
        <Text font="regular14" text={server.clientId} />
      </Stack>
      {server.clientSecret && (
        <Stack space={2}>
          <Text font="semibold14" text={t('clientSecret')} />
          <Text font="regular14" text={server.clientSecret} />
        </Stack>
      )}
    </Stack>
  );
};

interface MCPServerSidebarProps {
  existingServer?: MCPServer;
}

const MCPServerSidebar = ({ existingServer }: MCPServerSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const createMCPServerMutation = useCreateMCPServer();
  const updateMCPServerMutation = useUpdateMCPServer();
  const [newlyCreatedServer, setNewlyCreatedServer] = useState<MCPServer | null>(null);

  if (newlyCreatedServer) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader title={t('createMCPServerSuccess')} />
        <Sidebar.Content>
          <NewServerDetails server={newlyCreatedServer} />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={existingServer ? t('editMCPServer') : t('createMCPServer')} />
      <Sidebar.Content>
        <Stack space={4}>
          <Form
            onSubmit={async (values) => {
              if (existingServer) {
                await updateMCPServerMutation.mutateAsync({
                  clientId: existingServer.clientId,
                  label: values.label,
                  description: values.description,
                });
                hideSidebar();
              } else {
                const response = await createMCPServerMutation.mutateAsync({
                  label: values.label,
                  description: values.description,
                });
                setNewlyCreatedServer(response);
              }
            }}
          >
            <MCPServerForm existingServer={existingServer} />
          </Form>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    createMCPServer: 'Create MCP Server',
    editMCPServer: 'Edit MCP Server',
    createMCPServerSuccess: 'MCP Server created successfully',
    updateMCPServerSuccess: 'MCP Server updated successfully',
    name: 'Name',
    description: 'Description',
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    create: 'Create',
    save: 'Save',
    copy: 'Copy',
    copied: 'Copied',
  },
};
